import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tabs,
  Tab,
  Typography,
  InputAdornment,
  styled,
  CircularProgress,
  Container,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Invitation,
  InvitationState,
} from '../../../../redux/invitations/invitation.model';
import { getPolyglot } from '../../../../i18n';
import {
  useGetInvitationsQuery,
  useApproveInvitationMutation,
  useRejectInvitationMutation,
} from '../../../../redux/invitations/api/InvitationsAPI';
import moment from 'moment';
import { useAppSelector } from '../../../../redux/store.model';
import { getMyWorkspaceSelector } from '../../../../redux/stoerkID/selectors/StoerkId.selectors';
import InvitationRequestModal from './InvitationRequestModal';
import { getStatusColor, StyledChip } from './components/StyledChip';
import { useShowErrorMessage, useShowMessage } from '../../../../util/hooks';
import { useGroupsByWorkspaceId } from '../../../../redux/groups/hooks/useGroupByWorkspace';

const HeaderContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  border: 'none',
  padding: '0 2em 0 1em',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  '& td': {
    padding: '1rem',
    border: 'none',
  },
  '& td:first-of-type': {
    borderTopLeftRadius: '12px',
    borderBottomLeftRadius: '12px',
  },
  '& td:last-child': {
    borderTopRightRadius: '12px',
    borderBottomRightRadius: '12px',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const InvitationsAdministrator: React.FC = () => {
  const polyglot = getPolyglot();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvitation, setSelectedInvitation] =
    useState<Invitation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const showMessage = useShowMessage();
  const showErrorMessage = useShowErrorMessage();

  const workspace = useAppSelector(getMyWorkspaceSelector);
  const [approveInvitation, { isLoading: isApproving }] =
    useApproveInvitationMutation();
  const [rejectInvitation, { isLoading: isRejecting }] =
    useRejectInvitationMutation();

  // Fetch all invitations
  const {
    data: invitationsResponse,
    isLoading,
    error,
  } = useGetInvitationsQuery({
    workspaceId: workspace?.id || '',
  });

  const { data: companies = {} } = useGroupsByWorkspaceId(workspace?.id || '');

  // Filter invitations based on tab status and search query
  const filteredInvitations = useMemo(() => {
    if (!invitationsResponse) return [];

    let filtered = invitationsResponse;

    // Filter by tab status
    if (tabValue !== 0) {
      const statusFilter = (() => {
        switch (tabValue) {
          case 1:
            return InvitationState.NEW;
          case 2:
            return InvitationState.SENT;
          case 3:
            return InvitationState.APPROVED;
          case 4:
            return InvitationState.CANCELED;
          case 5:
            return InvitationState.REJECTED;
          default:
            return null;
        }
      })();

      if (statusFilter) {
        filtered = filtered.filter(
          (invitation) => invitation.status === statusFilter
        );
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((invitation) => {
        return (
          invitation.user?.name?.toLowerCase().includes(query) ||
          invitation.email?.toLowerCase().includes(query) ||
          invitation.companyName?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [invitationsResponse, tabValue, searchQuery]);

  // Paginate the filtered invitations
  const paginatedInvitations = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredInvitations.slice(startIndex, endIndex);
  }, [filteredInvitations, page, rowsPerPage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const formatDate = (date: number): string => {
    if (!date) return '-';
    return moment(date).format('YYYY/MM/DD');
  };

  const handleRowClick = (invitation: Invitation) => {
    setSelectedInvitation(invitation);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInvitation(null);
  };

  const handleReject = async (invitation: Invitation) => {
    try {
      await rejectInvitation({
        requestId: invitation.requestId,
        workspaceId: invitation.workspaceId,
        status: InvitationState.REJECTED,
      }).unwrap();
      showMessage(polyglot.t('freshwatch.invitation.reject_success'));
    } catch (error) {
      showErrorMessage(polyglot.t('freshwatch.invitation.reject_error'));
    } finally {
      handleCloseModal();
    }
  };

  const handleApprove = async (invitation: Partial<Invitation>) => {
    try {
      await approveInvitation(invitation).unwrap();
      showMessage(polyglot.t('freshwatch.invitation.approve_success'));
    } catch (error) {
      showErrorMessage(polyglot.t('freshwatch.invitation.approve_error'));
    } finally {
      handleCloseModal();
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        p: 3,
        height: '100%',
        backgroundColor: 'background.container',
      }}
    >
      <HeaderContainer>
        <Typography variant="h4" component="h1" gutterBottom>
          {polyglot.t('freshwatch.invitation.page_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {polyglot.t('freshwatch.invitation.page_description')}
        </Typography>
      </HeaderContainer>

      <TabsContainer>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="invitation status tabs"
        >
          <Tab
            label={polyglot.t('freshwatch.invitation.all_requests')}
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab
            label={polyglot.t('freshwatch.invitation.detail.NEW')}
            id="tab-1"
            aria-controls="tabpanel-1"
          />
          <Tab
            label={polyglot.t('freshwatch.invitation.detail.SENT')}
            id="tab-2"
            aria-controls="tabpanel-2"
          />
          <Tab
            label={polyglot.t('freshwatch.invitation.detail.APPROVED')}
            id="tab-3"
            aria-controls="tabpanel-3"
          />
          <Tab
            label={polyglot.t('freshwatch.invitation.detail.CANCELED')}
            id="tab-4"
            aria-controls="tabpanel-4"
          />
          <Tab
            label={polyglot.t('freshwatch.invitation.detail.REJECTED')}
            id="tab-5"
            aria-controls="tabpanel-5"
          />
        </Tabs>
      </TabsContainer>

      <SearchContainer>
        <TextField
          fullWidth
          placeholder={polyglot.t('freshwatch.invitation.search_placeholder')}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color={'primary'} />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          sx={{
            maxWidth: 400,
            backgroundColor: 'primary.contrastText',
            opacity: 1,
            borderRadius: '0.5rem',
          }}
        />
      </SearchContainer>

      <TabPanel value={tabValue} index={tabValue}>
        <TableContainer component={Paper} sx={{ border: 'none' }}>
          <Table
            sx={{
              minWidth: 650,
              backgroundColor: 'background.container',
              borderCollapse: 'separate',
              borderSpacing: '0 12px',
            }}
            aria-label="invitations table"
          >
            <TableHead>
              <TableRow
                sx={{
                  '& th': { paddingBottom: 0 },
                }}
              >
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.name')}
                </StyledTableCell>
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.email')}
                </StyledTableCell>
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.company_name')}
                </StyledTableCell>
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.phone_number')}
                </StyledTableCell>
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.request_date')}
                </StyledTableCell>
                <StyledTableCell>
                  {polyglot.t('freshwatch.invitation.detail.request_status')}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ color: 'error.main' }}
                  >
                    {polyglot.t('freshwatch.invitation.detail.error_loading')}
                  </TableCell>
                </TableRow>
              ) : filteredInvitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {polyglot.t('freshwatch.invitation.detail.no_results')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInvitations.map((invitation) => (
                  <StyledTableRow
                    key={invitation.userId}
                    onClick={() => handleRowClick(invitation)}
                  >
                    <TableCell>{invitation.user?.name || '-'}</TableCell>
                    <TableCell>{invitation.email || '-'}</TableCell>
                    <TableCell>{invitation.companyName}</TableCell>
                    <TableCell>
                      {invitation.user?.mobilePhoneNumber || '-'}
                    </TableCell>
                    <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                    <TableCell>
                      <StyledChip
                        sx={getStatusColor(
                          invitation.status as InvitationState
                        )}
                      >
                        {polyglot.t(
                          `freshwatch.invitation.detail.${invitation.status}`
                        )}
                      </StyledChip>
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredInvitations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ backgroundColor: 'background.default' }}
          />
        </TableContainer>
      </TabPanel>

      <InvitationRequestModal
        open={modalOpen}
        invitation={selectedInvitation}
        onClose={handleCloseModal}
        onReject={handleReject}
        onApprove={handleApprove}
        availableCompanies={companies || {}}
        isLoading={isApproving || isRejecting}
      />
    </Container>
  );
};

export default InvitationsAdministrator;
